import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components/Modals/Modal";
import { InputField } from "@/components/InputField/InputField";
import { TextArea } from "@/components/TextArea/TextArea";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import {
	useCreateCopywritingFrameworkMutation,
	useUpdateCopywritingFrameworkMutation,
} from "@/pages/Private/redux/copywritingFramework/copywritingFramework.api";

import {
	CopywritingFramework,
	CreateCopywritingFramework,
	CreateCopywritingFrameworkSchema,
	MessageType,
} from "../../schema/copywritingFramework";

interface CopywritingFrameworkModalProps {
	isOpen: boolean;
	framework: CopywritingFramework | null;
	onClose: () => void;
	onSuccess: (message: string) => void;
}

const MESSAGE_TYPES = Object.values(MessageType);

export const CopywritingFrameworkModal: FunctionComponent<CopywritingFrameworkModalProps> = ({
	isOpen,
	framework,
	onClose,
	onSuccess,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`copywritingFrameworks.${key}`), [t]);

	const isEdit = !!framework;

	const [createFramework, { isLoading: isCreating }] = useCreateCopywritingFrameworkMutation();
	const [updateFramework, { isLoading: isUpdating }] = useUpdateCopywritingFrameworkMutation();

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<CreateCopywritingFramework>({
		resolver: zodResolver(CreateCopywritingFrameworkSchema),
		defaultValues: {
			name: framework?.name ?? "",
			description: framework?.description ?? "",
			structure: framework?.structure ?? "",
			applicableTo: framework?.applicableTo ?? [],
			whenToUse: framework?.whenToUse ?? "",
			whenNotToUse: framework?.whenNotToUse ?? "",
		},
	});

	const onSubmit = async (values: CreateCopywritingFramework) => {
		try {
			if (isEdit && framework) {
				await updateFramework({ id: framework.id, ...values }).unwrap();
				onSuccess(ts("updated"));
			} else {
				await createFramework(values).unwrap();
				onSuccess(ts("created"));
			}

			onClose();
		} catch {
			onSuccess(ts("error"));
		}
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isCreating || isUpdating}
			isOpened={isOpen}
			size="md"
			submitButtonText={isEdit ? ts("editFramework") : ts("createFramework")}
			title={isEdit ? ts("editFramework") : ts("createFramework")}
		>
			<div className="flex flex-col gap-4">
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<InputField
							error={errors.name?.message}
							handleChange={(e) => field.onChange(e.target.value)}
							label={ts("form.name")}
							name="name"
							placeholder={ts("form.namePlaceholder")}
							showError={!!errors.name}
							value={field.value}
						/>
					)}
				/>

				<Controller
					control={control}
					name="description"
					render={({ field }) => (
						<TextArea
							error={errors.description?.message}
							handleChange={(e) => field.onChange(e.target.value)}
							label={ts("form.description")}
							name="description"
							placeholder={ts("form.descriptionPlaceholder")}
							showError={!!errors.description}
							value={field.value}
						/>
					)}
				/>

				<Controller
					control={control}
					name="structure"
					render={({ field }) => (
						<TextArea
							error={errors.structure?.message}
							handleChange={(e) => field.onChange(e.target.value)}
							label={ts("form.structure")}
							name="structure"
							placeholder={ts("form.structurePlaceholder")}
							showError={!!errors.structure}
							value={field.value}
						/>
					)}
				/>

				<Controller
					control={control}
					name="applicableTo"
					render={({ field }) => (
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-text">{ts("form.applicableTo")}</label>
							<div className="flex gap-4">
								{MESSAGE_TYPES.map((type) => (
									<Checkbox
										key={type}
										isChecked={field.value.includes(type)}
										name={`applicableTo-${type}`}
										onChange={() => {
											const current = field.value;
											const next = current.includes(type)
												? current.filter((v) => v !== type)
												: [...current, type];

											field.onChange(next);
										}}
									>
										{ts(`messageType.${type}`)}
									</Checkbox>
								))}
							</div>
							{errors.applicableTo && (
								<p className="text-xs text-status-error">{errors.applicableTo.message}</p>
							)}
						</div>
					)}
				/>

				<Controller
					control={control}
					name="whenToUse"
					render={({ field }) => (
						<TextArea
							error={errors.whenToUse?.message}
							handleChange={(e) => field.onChange(e.target.value)}
							label={ts("form.whenToUse")}
							name="whenToUse"
							placeholder={ts("form.whenToUsePlaceholder")}
							showError={!!errors.whenToUse}
							value={field.value}
						/>
					)}
				/>

				<Controller
					control={control}
					name="whenNotToUse"
					render={({ field }) => (
						<TextArea
							handleChange={(e) => field.onChange(e.target.value)}
							label={ts("form.whenNotToUse")}
							name="whenNotToUse"
							placeholder={ts("form.whenNotToUsePlaceholder")}
							showError={false}
							value={field.value ?? ""}
						/>
					)}
				/>
			</div>
		</Modal>
	);
};
